import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr'
import { type HubConnection } from '@microsoft/signalr'

import { getTokenFx } from '../model'

export class SignalRClient {
  private connection: HubConnection
  private reconnectTimer: NodeJS.Timeout | null = null
  private disconnectTimer: NodeJS.Timeout | null = null
  private shouldReconnect: boolean = true
  private reconnectDelay: number = 5000 // 5 секунд между попытками

  constructor(hubUrl: string) {
    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: getTokenFx,
        withCredentials: true,
      })
      .withAutomaticReconnect([0, 2000, 5000])
      .configureLogging(LogLevel.Information)
      .build()

    // Обработка событий соединения
    this.connection.onreconnecting(() => {
      console.debug('SignalR переподключение...')
    })

    this.connection.onreconnected(() => {
      console.debug('SignalR переподключен')
    })

    this.connection.onclose((error) => {
      console.log('SignalR соединение закрыто', error)
      // Запускаем автоматическое переподключение после закрытия
      this.scheduleReconnect()
    })
  }

  // Установка соединения
  async connect(): Promise<void> {
    console.debug('Попытка подключения к SignalR...')
    this.shouldReconnect = true
    await this.startConnection()
  }

  private async startConnection(): Promise<void> {
    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer)
      this.disconnectTimer = null
    }

    console.debug(
      `Текущее состояние SignalR соединения: ${this.connection.state}`,
    )

    try {
      if (this.connection.state === HubConnectionState.Disconnected) {
        console.debug('Установка SignalR соединения...')
        await this.connection.start()
        console.log('SignalR соединение установлено')
        // Сбрасываем задержку при успешном подключении
        this.reconnectDelay = 5000
      } else if (this.connection.state === HubConnectionState.Disconnecting) {
        console.debug(
          'SignalR соединение в процессе отключения, ждем завершения...',
        )
        this.scheduleReconnect()
      }
    } catch (error) {
      console.error('Ошибка подключения SignalR:', error)
      if (
        this.connection.state !== HubConnectionState.Disconnected &&
        this.connection.state !== HubConnectionState.Disconnecting
      ) {
        await this.connection.stop()
      }
      throw error
    }
  }

  // Планирование автоматического переподключения
  private scheduleReconnect(): void {
    if (!this.shouldReconnect) {
      return
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    console.debug(
      `Попытка переподключения через ${this.reconnectDelay / 1000} секунд...`,
    )

    this.reconnectTimer = setTimeout(() => {
      if (this.shouldReconnect) {
        console.debug('Попытка переподключения SignalR...')
        this.startConnection().catch((error) => {
          console.error('Не удалось переподключиться:', error)
          // Увеличиваем задержку с максимумом в 30 секунд
          this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000)
          this.scheduleReconnect()
        })
      }
    }, this.reconnectDelay)
  }

  // Отключение
  async disconnect(): Promise<void> {
    console.debug('Отключение от SignalR...')
    this.shouldReconnect = false

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.disconnectTimer) {
      clearTimeout(this.disconnectTimer)
      this.disconnectTimer = null
    }

    if (this.connection.state === HubConnectionState.Connected) {
      await this.connection.stop()
      console.log('SignalR соединение разорвано')
    } else if (
      this.connection.state === HubConnectionState.Connecting ||
      this.connection.state === HubConnectionState.Reconnecting
    ) {
      console.debug(
        'SignalR соединение в процессе подключения или переподключения, ждем завершения...',
      )
      this.disconnectTimer = setTimeout(() => void this.disconnect(), 1000) // Ждем 1 секунду перед повторной попыткой отключения
    }
  }

  // Подписка на события
  on(methodName: string, callback: (data: unknown) => void): void {
    if (!this.connection) {
      throw new Error('SignalR соединение не установлено')
    }
    this.connection.on(methodName, callback)
  }

  // Отписка от событий
  off(methodName: string): void {
    if (this.connection) {
      this.connection.off(methodName)
    }
  }

  // Отправка сообщения
  async invoke<T = unknown>(
    methodName: string,
    ...args: unknown[]
  ): Promise<T> {
    if (!this.connection) {
      throw new Error('SignalR соединение не установлено')
    }
    return await this.connection.invoke(methodName, ...args)
  }

  // Отправка сообщения без ожидания ответа
  async send(methodName: string, ...args: unknown[]): Promise<void> {
    if (!this.connection) {
      throw new Error('SignalR соединение не установлено')
    }
    await this.connection.send(methodName, ...args)
  }

  // Проверка статуса соединения
  get isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected
  }

  // Получение состояния соединения
  get connectionState(): HubConnectionState {
    return this.connection?.state || HubConnectionState.Disconnected
  }
}

// Создание экземпляра клиента (singleton)
export const createSignalRClient = (hubUrl: string): SignalRClient => {
  return new SignalRClient(hubUrl)
}

// Типы для TypeScript
export interface SignalRMessage<T = unknown> {
  type: string
  data: T
  timestamp: Date
}

export interface SignalREventHandler<T = unknown> {
  (data: T): void
}
